import { startOfDay } from 'date-fns';
import type { Valuation } from './filterValuations';
import { bisectRight } from 'd3-array';
export interface ValuationPoint {
  originalValue: number | null;
  value: number | null;
}
export interface ValuationsMap {
  valuationsByDate: Record<number, ValuationPoint>;
  valuationTimestamps: number[];
}
export interface TimestampValuationMap {
  timestamps: number[];
  valuationsByTimestamp: Record<number, Valuation>;
  firstValuation: Valuation | undefined;
}

/**
 * Gets the first and last valuations for domain boundaries
 */
const getBoundaryValuations = (
  valuations: Valuation[]
): [Valuation | null, Valuation | null] => {
  const firstValuation = valuations.at(-1) ?? null;
  const lastValuation = valuations.at(0) ?? null;
  return [firstValuation, lastValuation];
};

/**
 * Adds domain boundary values if they don't exist
 */
const addDomainBoundaries = (
  valuationsByDate: Record<number, ValuationPoint>,
  domainStart: number,
  domainEnd: number,
  firstValuation: Valuation | null,
  lastValuation: Valuation | null
) => {
  if (!valuationsByDate[domainStart]) {
    valuationsByDate[domainStart] = {
      originalValue: firstValuation?.originalFairValue ?? null,
      value: firstValuation?.fairValue ?? null,
    };
  }

  if (!valuationsByDate[domainEnd]) {
    valuationsByDate[domainEnd] = {
      originalValue: lastValuation?.originalFairValue ?? null,
      value: lastValuation?.fairValue ?? null,
    };
  }
};

/**
 * Creates a sorted list of timestamps
 */
const getSortedTimestamps = (
  valuationsByDate: Record<number, ValuationPoint>
): number[] => {
  return Object.keys(valuationsByDate)
    .map(Number)
    .sort((a, b) => a - b);
};

/**
 * Creates a map of timestamps to valuations and sorts timestamps
 */
const createTimestampValuationMap = (
  valuations: Valuation[]
): TimestampValuationMap => {
  const timestamps = valuations
    .map((valuation) => startOfDay(valuation.createdAt).getTime())
    .sort((a, b) => a - b);

  const valuationsByTimestamp = valuations.reduce(
    (acc, valuation) => {
      const timestamp = startOfDay(valuation.createdAt).getTime();
      acc[timestamp] = valuation;
      return acc;
    },
    {} as Record<number, Valuation>
  );

  return {
    timestamps,
    valuationsByTimestamp,
    firstValuation: valuations.at(-1),
  };
};

/**
 * Creates a valuation point from a valuation
 */
const createValuationPoint = (valuation: Valuation): ValuationPoint => ({
  originalValue: valuation.originalFairValue ?? null,
  value: valuation.fairValue ?? null,
});

/**
 * Finds the closest valuation timestamp that's before or equal to the target
 */
const findClosestValuationTimestamp = (
  timestamps: number[],
  targetTimestamp: number
): number | null => {
  const rightIndex = bisectRight(timestamps, targetTimestamp);
  return rightIndex > 0 ? timestamps[rightIndex - 1] : null;
};

/**
 * Creates the initial map of valuations by their dates
 */
const createInitialValuationsMap = (
  valuations: Valuation[],
  sharePriceTimestamps: number[]
): Record<number, ValuationPoint> => {
  const { timestamps, valuationsByTimestamp, firstValuation } =
    createTimestampValuationMap(valuations);

  return sharePriceTimestamps.reduce(
    (acc, targetTimestamp) => {
      const closestTimestamp = findClosestValuationTimestamp(
        timestamps,
        targetTimestamp
      );

      if (closestTimestamp) {
        const valuation = valuationsByTimestamp[closestTimestamp];
        acc[targetTimestamp] = createValuationPoint(valuation);
      } else if (firstValuation) {
        // Fallback to first valuation if no previous valuation exists
        acc[targetTimestamp] = createValuationPoint(firstValuation);
      } else {
        acc[targetTimestamp] = { originalValue: null, value: null };
      }

      return acc;
    },
    {} as Record<number, ValuationPoint>
  );
};

/**
 * Creates a map of valuations by date and ensures values exist at domain boundaries.
 * Returns both the map and sorted timestamps.
 */
export const createValuationsMap = (
  filteredValuations: Valuation[],
  sharePriceTimestamps: number[],
  domainStart: number,
  domainEnd: number
): ValuationsMap => {
  // Create the initial map of valuations
  const valuationsByDate = createInitialValuationsMap(
    filteredValuations,
    sharePriceTimestamps
  );

  // Add domain boundary values
  const [firstValuation, lastValuation] =
    getBoundaryValuations(filteredValuations);
  addDomainBoundaries(
    valuationsByDate,
    domainStart,
    domainEnd,
    firstValuation,
    lastValuation
  );

  // Create sorted list of timestamps
  const valuationTimestamps = getSortedTimestamps(valuationsByDate);

  return { valuationsByDate, valuationTimestamps };
};
